import internalApi from './internalApi'
import locationApi from './locationApi'

/* Moteur de pricing — servi par admin-service.
   Une grille active sert les estimations ; on la clone pour la faire évoluer. */

export interface Ruleset {
  id: number
  version: string
  actif: boolean
  margeTribeDefaut: number
  plafondMultiplicateur: number
  plancherMultiplicateur: number
  commentaire: string | null
  creeLe: string
  /* Collectes libres (hors mission) : Tips versés par geste (décision D4). */
  recompenseDecouverteTips?: number
  recompenseValidationTips?: number
  recompenseMiseAJourTips?: number
  /* Bornes de la récompense d'une collecte de mission, seuils de couverture. */
  plancherRecompenseTips?: number
  plafondRecompenseTips?: number
  plancherClientMajoration?: number
  seuilCouvertureBonne?: number
  seuilCouverturePartielle?: number
  /* Dernière activation (SUPER_ADMIN seulement) : date et email de l'auteur.
     Nulles pour une grille jamais activée depuis leur création. */
  activeeLe?: string | null
  activeePar?: string | null
}

export interface Coefficient {
  id: number
  facteur: 'richesse' | 'zone' | 'urgence' | 'exclusivite' | string
  cle: string
  multiplicateur: number
  /** 'spotter' | 'client' | 'both' */
  cible: string
}

export interface TarifDeBase {
  id: number
  /** Identifiants du référentiel des types de lieu : c'est par eux que le moteur joint (Z2-22). */
  categorieId: number | null
  sousTypeId: number | null
  /** Libellés recopiés, pour l'affichage d'une ligne restée hors référentiel. */
  categoryMain: string
  categorySub: string | null
  baseRewardTips: number
  baseCostCfa: number
  actif: boolean
}

export interface MargeCanal {
  id: number
  canal: string
  marge: number
  libelle: string | null
}

export interface Grille {
  ruleset: Ruleset
  coefficients: Coefficient[]
  tarifsDeBase: TarifDeBase[]
  margesCanal: MargeCanal[]
}

export interface PalierVitesse {
  rang: number
  multiplicateur: number
}

export interface Breakdown {
  rulesetVersion: string
  estimateLogId: number | null
  bonusVitesse: PalierVitesse[]
  recompenseSpotterTips: number
  recompenseSpotterFcfaEquivalent: number
  coutClientFcfaUnitaire: number
  coutClientFcfa: number
  margeFcfa: number
  margeEffectivePct: number
  niveauConfiance: string
  explication: string[]
}

export async function getGrilles(): Promise<Grille[]> {
  const res = await internalApi.get('/pricing/rules')
  return Array.isArray(res.data) ? res.data : []
}

export const clonerGrille = (id: number, version?: string) =>
  internalApi.post(`/pricing/rulesets/${id}/cloner`, version ? { version } : {})

/* Activer change toutes les estimations à partir de maintenant : le serveur
   exige une confirmation explicite (décision D3), sinon 400. */
export const activerGrille = (id: number) =>
  internalApi.post(`/pricing/rulesets/${id}/activer`, { confirme: true })

export const modifierGrille = (
  id: number,
  corps: {
    margeTribeDefaut?: number; plafondMultiplicateur?: number; plancherMultiplicateur?: number; commentaire?: string
    recompenseDecouverteTips?: number; recompenseValidationTips?: number; recompenseMiseAJourTips?: number
    plancherRecompenseTips?: number; plafondRecompenseTips?: number; plancherClientMajoration?: number
    seuilCouvertureBonne?: number; seuilCouverturePartielle?: number
  },
) => internalApi.put(`/pricing/rulesets/${id}`, corps)

export const supprimerGrille = (id: number) =>
  internalApi.delete(`/pricing/rulesets/${id}`)

export const modifierCoefficient = (id: number, multiplicateur: number) =>
  internalApi.put(`/pricing/coefficients/${id}`, { multiplicateur })

export const modifierTarif = (
  id: number,
  corps: { baseRewardTips?: number; baseCostCfa?: number; actif?: boolean },
) => internalApi.put(`/pricing/base-tariffs/${id}`, corps)

export const creerTarifFin = (
  rulesetId: number, categorieId: number, sousTypeId: number,
  baseRewardTips: number, baseCostCfa: number,
) => internalApi.post('/pricing/base-tariffs', {
  rulesetId, categorieId, sousTypeId, baseRewardTips, baseCostCfa,
})

export const supprimerTarif = (id: number) =>
  internalApi.delete(`/pricing/base-tariffs/${id}`)

/** Déclare ce que l'admin a finalement retenu. Instrumentation : n'échoue jamais bruyamment. */
export async function declarerValeurRetenue(logId: number, valeurRetenueTokensBase: number, questId?: string) {
  try {
    await internalApi.patch(`/pricing/log/${logId}/retenu`, { valeurRetenueTokensBase, questId })
  } catch {
    /* une ligne de calibration perdue ne doit pas gêner l'enregistrement d'une quête */
  }
}

export async function simuler(descripteur: Record<string, unknown>): Promise<Breakdown> {
  const res = await internalApi.post('/pricing/estimate', descripteur)
  return res.data
}

export interface PointEcart {
  date: string
  suggestion: number
  retenu: number
  ecartPct: number
  questId: string | null
}

export interface MetriquesPricing {
  /** Toutes les lignes du journal, simulations et collectes libres comprises. */
  estimationsTotal: number
  /** Ni simulation de cet écran, ni récompense de collecte libre. */
  estimationsHorsSimulation: number
  simulations: number
  recompensesCollecteLibre: number
  estimationsAvecValeurRetenue: number
  ecartMedianPct: number | null
  partMemeSensPct: number | null
  lectureDeLEcart: string
  serieEcarts: PointEcart[]
  retoursTotal: number
  retoursSpottersInsuffisants: number
  retoursDevisRefusePrix: number
  fiabilite: {
    pourcentage: number
    missionsConfrontees: number
    missionsNecessaires: number
    estimationsAvecIssue: number
    estimationsNecessaires: number
    niveauConfiance: string
    ceQuiManque: string[]
  }
}

export interface MetriquesQuetes {
  quetesPubliees: number
  avecInstantane: number
  missionsConfrontees: number
  missionsPourvues: number
  tauxPourvuesPct: number | null
  avecSuggestionMoteur: number
  suggestionSuivieTelleQuelle: number
  margeEffectiveMoyennePct: number | null
}

/* Les métriques viennent des DEUX services : admin-service sait ce que le
   moteur a proposé, location-service sait si les missions se sont pourvues.
   Le navigateur parle déjà aux deux ; les faire dialoguer entre eux aurait
   mis une dépendance réseau sur l'affichage d'un tableau de bord. */
export async function getMetriquesQuetes(): Promise<MetriquesQuetes | null> {
  try {
    const res = await locationApi.get('/quest/metriques-pricing')
    return res.data
  } catch {
    return null
  }
}

export async function getMetriques(missionsConfrontees: number): Promise<MetriquesPricing> {
  const res = await internalApi.get('/pricing/metriques', { params: { missionsConfrontees } })
  return res.data
}

export const enregistrerRetour = (corps: {
  issue: string; questId?: string; valeurReelle?: number; commentaire?: string
}) => internalApi.post('/pricing/feedback', corps)

export function messageErreur(err: any, defaut: string): string {
  const d = err?.response?.data
  if (typeof d === 'string' && d.trim()) return d.trim()
  if (d && typeof d.message === 'string' && d.message.trim()) return d.message.trim()
  return defaut
}

/* ── Rendre un coefficient compréhensible sans lire le code ──────────────
   Chaque ligne de la grille doit dire ce qu'elle multiplie, dans quel sens,
   et sur quel montant. Un « ×1,4 sur urgence » ne veut rien dire tout seul. */

const LIBELLE_CLE: Record<string, string> = {
  champ_supplementaire: 'chaque champ demandé au-delà de trois',
  champ_photo: 'une photo est demandée',
  champ_prix: 'un relevé de prix est demandé',
  champ_gps: 'un point GPS est demandé',
  urbaine_dense: 'la zone est urbaine dense',
  peri_urbaine: 'la zone est péri-urbaine',
  rurale_isolee: 'la zone est rurale isolée',
  urbaine_peripherique: 'la zone est urbaine périphérique',
  rurale: 'la zone est rurale',
  standard: 'le délai est standard, 30 jours ou plus',
  rapide_15j: 'le délai est de 15 jours',
  express_7j: 'le délai est de 7 jours',
  '7j': 'le délai est de 7 jours',
  '48h': 'le délai est de 48 heures',
  couverture_bonne: 'la zone est déjà bien couverte, plus de 60 %',
  couverture_partielle: 'la zone est partiellement couverte, 30 à 60 %',
  zone_aveugle: 'la zone est aveugle, moins de 30 % de couverture',
  NON_EXCLUSIF: 'la donnée est revendable',
  EXCLUSIF_6_MOIS: 'la donnée est exclusive 6 mois',
  EXCLUSIF_TEMPORAIRE_3_MOIS: "la donnée est exclusive 3 mois",
  EXCLUSIF_PERMANENT: 'la donnée est exclusive définitivement',
}

export function expliquerCoefficient(c: Coefficient): string {
  const quand = LIBELLE_CLE[c.cle] ?? `« ${c.cle} »`
  if (c.multiplicateur === 1) {
    return `Neutre : quand ${quand}, rien ne change. Posez une valeur pour que ce cas compte.`
  }
  const sens = c.multiplicateur > 1 ? 'augmente' : 'diminue'
  const pct = Math.abs(Math.round((c.multiplicateur - 1) * 100))
  const quoi = c.cible === 'spotter'
    ? 'la récompense du Spotter, sans toucher au prix client'
    : c.cible === 'client'
      ? 'le prix client, sans toucher à la récompense du Spotter'
      : 'la récompense du Spotter, et le prix client par ricochet'
  return `Quand ${quand}, ${sens} ${quoi} de ${pct} %.`
}

export const LIBELLE_FACTEUR: Record<string, string> = {
  richesse: 'Richesse de la collecte — ce qu’on demande au Spotter de relever',
  zone: 'Zone — difficulté d’accès et densité',
  urgence: 'Urgence — valeur d’un délai court pour le client',
  exclusivite: 'Exclusivité — la donnée est-elle revendue à d’autres',
  deficit: 'Déficit d’information — ce que Tribe ignore encore de la zone',
}
